import { Shield, CheckCircle, Key, Users, Lock, Globe, AlertTriangle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function AuthenticationPage() {

  // Mock authentication data
  const authMethods = [
    {
      id: 1,
      name: 'Single Sign-On (SSO)',
      provider: 'Okta',
      type: 'SAML 2.0',
      status: 'active',
      users: 156,
      lastSync: '5 minutes ago',
      icon: Shield,
      color: 'text-blue-400'
    },
    {
      id: 2,
      name: 'Multi-Factor Authentication',
      provider: 'Line4 MFA',
      type: 'TOTP',
      status: 'active',
      users: 156,
      lastSync: 'Real-time',
      icon: Lock,
      color: 'text-green-400'
    },
    {
      id: 3,
      name: 'OAuth 2.0',
      provider: 'Google Workspace',
      type: 'OAuth',
      status: 'active',
      users: 42,
      lastSync: '2 minutes ago',
      icon: Key,
      color: 'text-yellow-400'
    },
    {
      id: 4,
      name: 'LDAP Directory',
      provider: 'Active Directory',
      type: 'LDAP',
      status: 'warning',
      users: 89,
      lastSync: '1 hour ago',
      icon: Users,
      color: 'text-orange-400'
    }
  ];

  const totalUsers = authMethods.reduce((sum, method) => sum + method.users, 0);
  const activeUsers = 142; // Mock active sessions

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-green-500/20">
            <Shield className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Authentication & Security</h1>
            <p className="text-neutral-400">Identity providers and access control methods</p>
          </div>
        </div>
        <button className="btn-primary">
          Add Auth Method
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Total Users</p>
              <p className="text-2xl font-bold text-white mt-1">{totalUsers}</p>
            </div>
            <Users className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Active Sessions</p>
              <p className="text-2xl font-bold text-white mt-1">{activeUsers}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Auth Methods</p>
              <p className="text-2xl font-bold text-white mt-1">{authMethods.length}</p>
            </div>
            <Key className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">MFA Enabled</p>
              <p className="text-2xl font-bold text-white mt-1">100%</p>
            </div>
            <Shield className="w-8 h-8 text-cyan-400" />
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Security Policy</h2>
        <div className="space-y-4">
          <div>
            <label className="form-label">Password Policy</label>
            <select className="form-input w-full">
              <option>Basic - 8 characters minimum</option>
              <option selected>Standard - 12 characters, mixed case, numbers</option>
              <option>Strong - 16 characters, symbols required</option>
              <option>Enterprise - Custom regex pattern</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Session Timeout</label>
              <select className="form-input w-full">
                <option>15 minutes</option>
                <option>30 minutes</option>
                <option selected>1 hour</option>
                <option>8 hours</option>
                <option>Never</option>
              </select>
            </div>
            <div>
              <label className="form-label">Max Login Attempts</label>
              <select className="form-input w-full">
                <option>3 attempts</option>
                <option selected>5 attempts</option>
                <option>10 attempts</option>
                <option>Unlimited</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="require-mfa" className="rounded border-neutral-700" defaultChecked />
              <label htmlFor="require-mfa" className="text-sm text-neutral-300">
                Require Multi-Factor Authentication for all users
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="ip-whitelist" className="rounded border-neutral-700" />
              <label htmlFor="ip-whitelist" className="text-sm text-neutral-300">
                Enable IP address whitelisting
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="geo-blocking" className="rounded border-neutral-700" />
              <label htmlFor="geo-blocking" className="text-sm text-neutral-300">
                Enable geographic access restrictions
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="audit-logs" className="rounded border-neutral-700" defaultChecked />
              <label htmlFor="audit-logs" className="text-sm text-neutral-300">
                Log all authentication attempts (required for compliance)
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Authentication Methods */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Authentication Methods</h2>
          <button className="btn-outline text-sm">Configure Priority</button>
        </div>

        <div className="space-y-4">
          {authMethods.map((method) => {
            const Icon = method.icon;
            return (
              <div
                key={method.id}
                className="bg-neutral-900 rounded-lg p-4 border border-neutral-800 hover:border-neutral-700 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-3 bg-neutral-800 rounded-lg">
                      <Icon className={cn("w-6 h-6", method.color)} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-white font-semibold">{method.name}</h3>
                        <span className={cn(
                          "badge",
                          method.status === 'active' ? 'badge-success' : 'badge-warning'
                        )}>
                          {method.status}
                        </span>
                        <span className="badge bg-neutral-800 text-neutral-300 border border-neutral-700">
                          {method.type}
                        </span>
                      </div>
                      <p className="text-sm text-neutral-400 mb-3">Provider: {method.provider}</p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-neutral-500">Active Users</p>
                          <p className="text-lg font-semibold text-white">{method.users}</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500">Last Sync</p>
                          <p className="text-sm text-neutral-300 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {method.lastSync}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500">Auth Rate</p>
                          <p className="text-lg font-semibold text-green-400">99.2%</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-outline text-xs px-3 py-1">Configure</button>
                    <button className="btn-outline text-xs px-3 py-1 text-neutral-400 border-neutral-700">
                      Test
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Authentication Events */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Recent Authentication Events</h2>
        <div className="space-y-3">
          {[
            { user: 'alice.cooper@line4.ai', event: 'SSO Login Success', method: 'Okta SAML', ip: '192.168.1.45', time: '1 min ago', status: 'success' },
            { user: 'bob.marley@line4.ai', event: 'MFA Challenge Completed', method: 'TOTP', ip: '10.0.0.23', time: '3 min ago', status: 'success' },
            { user: 'charlie.brown@line4.ai', event: 'OAuth Login Success', method: 'Google', ip: '172.16.0.8', time: '5 min ago', status: 'success' },
            { user: 'david.jones@external.com', event: 'Login Failed - Invalid Credentials', method: 'LDAP', ip: '203.0.113.42', time: '8 min ago', status: 'failed' },
            { user: 'emma.watson@line4.ai', event: 'Password Reset', method: 'Email', ip: '192.168.1.56', time: '12 min ago', status: 'warning' },
            { user: 'frank.sinatra@line4.ai', event: 'Session Expired', method: 'SSO', ip: '10.0.0.89', time: '15 min ago', status: 'info' }
          ].map((event, idx) => (
            <div key={idx} className="bg-neutral-900 rounded-lg p-3 border border-neutral-800 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                {event.status === 'success' && <CheckCircle className="w-5 h-5 text-green-400" />}
                {event.status === 'failed' && <AlertTriangle className="w-5 h-5 text-red-400" />}
                {event.status === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-400" />}
                {event.status === 'info' && <Globe className="w-5 h-5 text-blue-400" />}
                <div className="flex-1">
                  <p className="text-white font-medium">{event.event}</p>
                  <p className="text-sm text-neutral-400">
                    {event.user} • {event.method} • {event.ip}
                  </p>
                </div>
                <span className="text-sm text-neutral-500">{event.time}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance Status */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Compliance & Audit</h2>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-neutral-400 mb-3">Authentication Standards</h3>
            <div className="space-y-2">
              {[
                { name: 'SOC 2 Type II', status: 'compliant' },
                { name: 'ISO 27001', status: 'compliant' },
                { name: 'NIST 800-63B', status: 'compliant' },
                { name: 'GDPR Article 32', status: 'compliant' }
              ].map((standard, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-neutral-900 rounded">
                  <span className="text-sm text-neutral-300">{standard.name}</span>
                  <span className="badge badge-success text-xs">
                    {standard.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-neutral-400 mb-3">Audit Trail</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-neutral-900 rounded">
                <span className="text-sm text-neutral-300">Total Auth Events (24h)</span>
                <span className="text-sm font-semibold text-white">2,847</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-neutral-900 rounded">
                <span className="text-sm text-neutral-300">Failed Attempts (24h)</span>
                <span className="text-sm font-semibold text-yellow-400">23</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-neutral-900 rounded">
                <span className="text-sm text-neutral-300">Blocked IPs (Active)</span>
                <span className="text-sm font-semibold text-red-400">4</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-neutral-900 rounded">
                <span className="text-sm text-neutral-300">Last Audit Review</span>
                <span className="text-sm font-semibold text-cyan-400">2 days ago</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
